export const PARAMETERS_VERSION_KEY = 'parametersVersion';
export const PARAMETERS_FORMAT_KEY = 'parametersPersistFormat';
export const PARAMETERS_FORMAT = 'snapshot-1';

export function unwrapParametersPayload (data) {
  if (!data || typeof data !== 'object') {
    return {};
  }

  // An explicit success: false means body (if any) is error detail, not a parameters payload -
  // don't trust it. Flat responses without a success field (or success: true) are unaffected.
  if (data.success === false) {
    return {};
  }

  if (data.body && typeof data.body === 'object') {
    return data.body;
  }

  return data;
}

export function normalizeParameterList (parameters) {
  if (Array.isArray(parameters)) {
    const map = {};

    parameters.forEach(parameter => {
      if (!parameter || parameter.key == null || parameter.key === '' || Object.prototype.hasOwnProperty.call(map, parameter.key)) {
        return;
      }

      let val = parameter.value;

      if (String(val).toLowerCase() === 'true') {
        val = true;
      } else if (String(val).toLowerCase() === 'false') {
        val = false;
      }

      map[parameter.key] = val;
    });

    return map;
  }

  if (parameters && typeof parameters === 'object') {
    return parameters;
  }

  return {};
}

export function toMessageRows (rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.filter(item => item && item.key != null && item.key !== '');
}

export function versionToSend (storedRows, storedVersion, persistFormat) {
  if (persistFormat !== PARAMETERS_FORMAT) {
    return null;
  }

  if (!Array.isArray(storedRows) || !storedRows.length || storedVersion == null || storedVersion === '') {
    return null;
  }

  return storedVersion;
}

// Read at request time, not construction time: LocalStorage.clear() on deploy
// runs after EnvironmentManager's constructor has already imported.
export function readPersistedParametersMeta (storage) {
  if (!storage || typeof storage.getItem !== 'function') {
    return { version: null, format: null };
  }

  const version = storage.getItem(PARAMETERS_VERSION_KEY);
  const format = storage.getItem(PARAMETERS_FORMAT_KEY);

  return {
    version: version == null || version === '' ? null : version,
    format: format == null || format === '' ? null : format
  };
}

function toCloneableValue (value) {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  return value == null ? '' : String(value);
}

export function toIdbParameterRows (rows) {
  const seen = new Set();

  return toMessageRows(rows)
    .filter(item => !seen.has(item.key) && seen.add(item.key))
    .map(item => ({ key: String(item.key), value: toCloneableValue(item.value) }));
}

export function resolveParametersFromResponse ({ data, currentParameters, storedRows }) {
  const payload = unwrapParametersPayload(data);
  const { version, parameters } = payload;
  const nextVersion = version != null && version !== '' ? String(version) : null;
  const hasList = Array.isArray(parameters) && parameters.length > 0;

  if (hasList) {
    return {
      parameters: normalizeParameterList(parameters),
      rows: parameters,
      version: nextVersion,
      didUpdateList: true
    };
  }

  const fromStore = Array.isArray(storedRows) && storedRows.length
    ? normalizeParameterList(storedRows)
    : null;

  return {
    parameters: fromStore || currentParameters || {},
    rows: storedRows,
    version: nextVersion,
    didUpdateList: false
  };
}

export function planParametersSync ({ data, currentParameters, storedRows, storedVersion, persistFormat }) {
  const resolved = resolveParametersFromResponse({
    data,
    currentParameters,
    storedRows
  });
  const rows = toMessageRows(resolved.rows);

  return {
    requestVersion: versionToSend(storedRows, storedVersion, persistFormat),
    rows,
    version: resolved.version,
    parameters: resolved.parameters,
    shouldApply: rows.length > 0,
    shouldPersist: resolved.didUpdateList && rows.length > 0
  };
}

// True only for a real cache-hit confirmation: the server echoed back the exact version we
// already have stored, with no list attached. A response with a DIFFERENT version but no list
// means the backend is inconsistent (version bumped, data not propagated) - that must return
// false, or the caller would adopt a version it has no matching data for and never re-fetch.
export function shouldConfirmCachedVersion (plan, currentVersion) {
  return !plan.shouldPersist && plan.version != null && plan.version === currentVersion;
}
